import { Link } from "react-router-dom";
import { useState } from "react";
import {
  CalendarRange,
  MessageCircle,
  CreditCard,
  Users,
  BarChart3,
  IdCard,
  Star,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Download,
} from "lucide-react";
import Button from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { useAuth } from "../context/AuthContext";

const FEATURES = [
  {
    icon: CalendarRange,
    title: "Smart calendar",
    description: "Manage every booking, staff shift, and no-show in one drag-and-drop calendar.",
  },
  {
    icon: MessageCircle,
    title: "WhatsApp confirmations",
    description: "Automatic booking confirmations and reminders sent straight to your clients' WhatsApp.",
  },
  {
    icon: Users,
    title: "Client management",
    description: "Keep notes, tags, and visit history so every client feels remembered.",
  },
  {
    icon: IdCard,
    title: "Staff scheduling",
    description: "Add staff, assign services, and let clients book with their favorite pro.",
  },
  {
    icon: CreditCard,
    title: "Payments & deposits",
    description: "Take deposits and mobile money payments online to cut down on cancellations.",
  },
  {
    icon: BarChart3,
    title: "Business insights",
    description: "See revenue, rebooking rate, and busy hours at a glance.",
  },
];

const STATS = [
  { value: "2,400+", label: "businesses booked" },
  { value: "98%", label: "confirmation rate" },
  { value: "35%", label: "fewer no-shows" },
  { value: "5 min", label: "to get set up" },
];

const STEPS = [
  {
    step: "01",
    title: "Create your profile",
    description: "Add your services, prices, and photos — no credit card required.",
  },
  {
    step: "02",
    title: "Share your booking link",
    description: "Post it on Instagram, WhatsApp, or your website and start taking bookings today.",
  },
  {
    step: "03",
    title: "Let SHUWA run the rest",
    description: "Automatic reminders, staff schedules, and payments happen without lifting a finger.",
  },
];

const FAQS = [
  {
    q: "Is SHUWA really free for solo businesses?",
    a: "Yes — solo businesses can use SHUWA's calendar, client management, and WhatsApp confirmations for free, forever. No credit card required to get started.",
  },
  {
    q: "How long does it take to set up?",
    a: "Most businesses are up and running in about 5 minutes. Add your services and prices, share your booking link, and you're ready to take bookings.",
  },
  {
    q: "Will my clients get automatic reminders?",
    a: "Yes. SHUWA sends automatic WhatsApp confirmations and reminders to your clients before their appointment, which has been shown to cut no-shows by up to 35%.",
  },
  {
    q: "Can I add staff members later?",
    a: "Yes. You can add staff at any time, assign them services, and let clients book directly with their preferred pro.",
  },
  {
    q: "Do you support online payments and deposits?",
    a: "Yes. SHUWA lets you take mobile money payments and deposits online to reduce last-minute cancellations.",
  },
  {
    q: "What if I work alone as a freelancer?",
    a: "You can sign up as a freelancer instead of a business — it uses the same free dashboard, just tailored for solo providers.",
  },
];

export default function ForBusiness() {
  const { user } = useAuth();
  const [openFaq, setOpenFaq] = useState(0);
  const getStartedTo = (type = "business") =>
    !user
      ? `/register?role=business_owner&type=${type}&next=/business/onboarding`
      : user.role === "business_owner"
      ? "/business"
      : `/business/onboarding?type=${type}`;

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative bg-slate-900 text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[560px] -translate-x-1/2 rounded-full bg-primary/25 blur-[120px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] [background-size:40px_40px]"
        />

        <div className="relative mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 sm:py-28 md:grid-cols-2 md:items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary-foreground/90 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Free forever for solo businesses
            </span>

            <h1 className="mt-5 font-montserrat text-3xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              You run your business,
              <br />
              SHUWA run your bookings.
            </h1>
            <p className="mt-5 max-w-md text-base text-slate-300 sm:text-lg">
              SHUWA for Business gives you a calendar, staff, clients, and payments — with
              automatic WhatsApp confirmations — all in one free dashboard.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button as={Link} to={getStartedTo("business")} size="lg" className="shadow-lg shadow-primary/30">
                Get started free
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button
                as={Link}
                to="/login?next=/business"
                variant="outline"
                size="lg"
                className="border-white/30 text-white hover:bg-white/10"
              >
                Log in
              </Button>
              <Button
                as={Link}
                to="/download"
                variant="outline"
                size="lg"
                className="border-white/30 text-white hover:bg-white/10"
              >
                <Download className="h-4 w-4" />
                Download app
              </Button>
            </div>

            <p className="mt-4 text-sm text-slate-400">
              Working solo?{" "}
              <Link to={getStartedTo("freelance")} className="font-medium text-primary hover:text-primary/80">
                Sign up as a freelancer
              </Link>
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-400">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-primary" /> No credit card required
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-primary" /> Set up in 5 minutes
              </span>
            </div>
          </div>

          <div className="relative">
            <div
              aria-hidden
              className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-primary/30 via-transparent to-transparent blur-2xl"
            />
            <Card className="relative border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold uppercase tracking-wide text-slate-300">
                  Today at your business
                </p>
                <span className="rounded-full bg-emerald-400/15 px-2.5 py-1 text-[11px] font-semibold text-emerald-300">
                  Live
                </span>
              </div>
              <div className="mt-4 space-y-3">
                {[
                  { time: "9:00", label: "Haircut — Amara K.", status: "Confirmed" },
                  { time: "11:30", label: "Braids — Chidi O.", status: "Confirmed" },
                  { time: "14:00", label: "Manicure — Zuri M.", status: "Pending" },
                ].map((row) => (
                  <div
                    key={row.time}
                    className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-xl bg-white/10 px-4 py-3 text-sm transition-colors hover:bg-white/15"
                  >
                    <span className="font-semibold tabular-nums">{row.time}</span>
                    <span className="min-w-0 flex-1 truncate text-slate-200">{row.label}</span>
                    <span
                      className={
                        row.status === "Confirmed"
                          ? "rounded-full bg-emerald-400/15 px-2 py-0.5 text-xs font-medium text-emerald-300"
                          : "rounded-full bg-amber-400/15 px-2 py-0.5 text-xs font-medium text-amber-300"
                      }
                    >
                      {row.status}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-xl border border-dashed border-white/15 px-4 py-3 text-xs text-slate-300">
                <MessageCircle className="h-4 w-4 shrink-0 text-primary" />
                WhatsApp reminder sent to Zuri M. — 2 hours before her appointment.
              </div>
            </Card>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative border-t border-white/10">
          <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-4 py-8 sm:px-6 md:grid-cols-4">
            {STATS.map((stat) => (
              <div key={stat.label} className="text-center md:text-left">
                <p className="font-montserrat text-2xl font-bold sm:text-3xl">{stat.value}</p>
                <p className="mt-1 text-xs text-slate-400 sm:text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-semibold uppercase tracking-wide text-primary">Everything included</span>
          <h2 className="mt-2 font-montserrat text-2xl font-bold text-slate-900 sm:text-3xl">
            Everything you need to manage your business
          </h2>
          <p className="mt-3 text-sm text-muted-foreground sm:text-base">
            One dashboard for calendar, staff, clients, and payments — built for busy service businesses.
          </p>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, description }) => (
            <Card
              key={title}
              className="group p-6 transition-all duration-200 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-montserrat text-base font-bold text-slate-900">{title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-muted/40">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-xs font-semibold uppercase tracking-wide text-primary">How it works</span>
            <h2 className="mt-2 font-montserrat text-2xl font-bold text-slate-900 sm:text-3xl">
              Up and running before your next client walks in
            </h2>
          </div>

          <div className="relative mt-12 grid gap-8 md:grid-cols-3">
            <div
              aria-hidden
              className="absolute left-0 right-0 top-6 hidden h-px bg-border md:block"
            />
            {STEPS.map((item) => (
              <div key={item.step} className="relative text-center md:text-left">
                <div className="relative z-10 mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-900 font-montserrat text-sm font-bold text-white md:mx-0">
                  {item.step}
                </div>
                <h3 className="mt-4 font-montserrat text-base font-bold text-slate-900">{item.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6 sm:py-24">
        <Card className="p-8 text-center sm:p-12">
          <div className="flex justify-center gap-1 text-amber-400">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-current" />
            ))}
          </div>
          <p className="mt-5 font-montserrat text-lg font-medium text-slate-900 sm:text-xl">
            "SHUWA cut our no-shows in half. Clients get a WhatsApp reminder automatically and I
            haven't touched a paper booking book in months."
          </p>
          <p className="mt-4 text-sm font-semibold text-slate-900">Amara K.</p>
          <p className="text-sm text-muted-foreground">Owner, Amara's Hair Studio</p>
        </Card>
      </section>

      {/* FAQ */}
      <section className="bg-muted/40">
        <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6 sm:py-24">
          <div className="text-center">
            <span className="text-xs font-semibold uppercase tracking-wide text-primary">FAQ</span>
            <h2 className="mt-2 font-montserrat text-2xl font-bold text-slate-900 sm:text-3xl">
              Frequently asked questions
            </h2>
          </div>

          <div className="mt-10 space-y-3">
            {FAQS.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <Card key={item.q} className="overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                    aria-expanded={isOpen}
                  >
                    <span className="text-sm font-semibold text-slate-900 sm:text-base">{item.q}</span>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <p className="px-5 pb-4 text-sm text-muted-foreground">{item.a}</p>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-slate-900">
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-primary/30 blur-[110px]"
        />
        <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-4 px-4 py-16 text-center sm:px-6 sm:py-24">
          <h2 className="font-montserrat text-2xl font-bold text-white sm:text-3xl">
            Ready to get more bookings?
          </h2>
          <p className="max-w-md text-sm text-slate-300">
            Set up your business profile in minutes — no credit card required.
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            <Button as={Link} to={getStartedTo("business")} size="lg" className="shadow-lg shadow-primary/30">
              List your business
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              as={Link}
              to={getStartedTo("freelance")}
              variant="outline"
              size="lg"
              className="border-white/30 text-white hover:bg-white/10"
            >
              Sign up as a freelancer
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
